import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ScopeSwitcher } from '../components/ScopeSwitcher';
import { useScope } from '../lib/hooks';
import { formatShoppingDate, listsApi, shoppingDateInput, type DaySummary, type ShoppingListSummary } from '../lib/domain';
import { storesApi, type StoreBranch } from '../lib/domain';

export function ListsPage() {
  const [scope] = useScope();
  const [status, setStatus] = useState<'OPEN' | 'ARCHIVED'>('OPEN');
  const [lists, setLists] = useState<ShoppingListSummary[]>([]);
  const [fromCache, setFromCache] = useState(false);
  const [branches, setBranches] = useState<StoreBranch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [newName, setNewName] = useState('');
  const [newDate, setNewDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [dayOpen, setDayOpen] = useState(false);
  const [dayDate, setDayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [day, setDay] = useState<DaySummary | null>(null);

  useEffect(() => {
    if (!scope) return;
    setLoading(true);
    listsApi
      .list(scope, status)
      .then((r) => {
        setLists(r.data.lists);
        setFromCache(r.fromCache);
      })
      .finally(() => setLoading(false));
  }, [scope, status]);

  useEffect(() => {
    storesApi
      .searchBranches('')
      .then((r) => setBranches(r.branches))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!scope || !dayOpen || !dayDate) return;
    setDay(null);
    listsApi.daySummary(scope, dayDate).then((r) => setDay(r.day)).catch(() => setDay(null));
  }, [scope, dayOpen, dayDate]);

  async function createList() {
    if (!scope) return;
    const isGroup = scope.startsWith('group:');
    const res = await listsApi.create(scope, selectedBranch || undefined, newName.trim() || undefined, isGroup ? scope.slice(6) : undefined, newDate || undefined);
    setLists((prev) => [res.list, ...prev]);
    setNewName('');
    setNewDate('');
  }

  return (
    <div className="stack">
      <h1>Listen</h1>
      <ScopeSwitcher />

      <div className="card stack">
        <h2>Neue Liste</h2>
        <div className="field">
          <label htmlFor="list-name">Name (optional)</label>
          <input id="list-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="z. B. Wocheneinkauf" />
        </div>
        <div className="field">
          <label htmlFor="branch">Filiale (optional)</label>
          <select id="branch" value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}>
            <option value="">Ohne Filiale</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.chain.name} - {b.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="list-date">Einkaufsdatum (optional)</label>
          <input id="list-date" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
        </div>
        <button className="primary" onClick={createList}>
          Liste anlegen
        </button>
      </div>

      <div className="card stack">
        <button onClick={() => setDayOpen((o) => !o)} aria-expanded={dayOpen}>
          📅 Tag {dayOpen ? '▲' : '▼'}
        </button>
        {dayOpen && (
          <>
            <input type="date" value={dayDate} onChange={(e) => setDayDate(e.target.value)} aria-label="Einkaufstag" />
            {day && day.lists.length === 0 && <p className="muted">Für diesen Tag gibt es keine Einkaufslisten.</p>}
            <ul className="list-reset stack">
              {day?.lists.map(({ list }) => (
                <li key={list.id}>
                  <Link to={`/lists/${list.id}`}>{list.name || 'Ohne Namen'}</Link>
                  <span className="muted"> · {list.storeBranch ? `${list.storeBranch.chain.name} - ${list.storeBranch.name}` : 'Ohne Filiale'}</span>
                </li>
              ))}
            </ul>
            {day && day.products.length > 0 && (
              <p className="muted">
                {day.products.length} Produkt(e) zugeordnet, {day.products.filter((p) => !p.listId).length} ohne Liste.
              </p>
            )}
            <Link to={`/days/${dayDate}?scope=${encodeURIComponent(scope ?? '')}`}>Tagesübersicht &amp; Produkte verteilen</Link>
          </>
        )}
      </div>

      <div className="row">
        <button onClick={() => setStatus('OPEN')} aria-pressed={status === 'OPEN'}>
          Offen
        </button>
        <button onClick={() => setStatus('ARCHIVED')} aria-pressed={status === 'ARCHIVED'}>
          Archiviert
        </button>
      </div>
      {fromCache && <p className="muted">Offline-Ansicht (zuletzt synchronisierter Stand)</p>}

      {loading ? (
        <p>Lade…</p>
      ) : lists.length === 0 ? (
        <p className="muted">Keine Listen vorhanden.</p>
      ) : (
        <ul className="list-reset stack">
          {lists.map((l) => (
            <li key={l.id} className="card">
              <div className="row between">
                <Link to={`/lists/${l.id}`}>
                  <strong>{l.memoScopeKey ? '📝 Merkliste' : l.name || 'Ohne Namen'}</strong>
                </Link>
                {l.memoScopeKey ? (
                  <span className="badge">Nächster Einkauf</span>
                ) : (
                  <span className={`badge ${l.status === 'OPEN' ? '' : 'ok'}`}>{l.status === 'OPEN' ? 'Offen' : 'Abgeschlossen'}</span>
                )}
              </div>
              <div className="muted">
                {l.memoScopeKey ? '' : `${l.storeBranch ? `${l.storeBranch.chain.name} - ${l.storeBranch.name}` : 'Ohne Filiale'} · `}
                {l._count?.items ?? 0} Artikel
              </div>
              {l.shoppingDate && (
                <div className="muted">
                  Einkauf am {formatShoppingDate(l.shoppingDate)}
                  {l.ownerGroupId && (
                    <>
                      {' · '}
                      <Link to={`/days/${shoppingDateInput(l.shoppingDate)}?scope=${encodeURIComponent(`group:${l.ownerGroupId}`)}`}>Tagesübersicht</Link>
                    </>
                  )}
                </div>
              )}
              {l.status === 'ARCHIVED' && <Link to={`/lists/${l.id}/summary`}>Abschlussübersicht</Link>}
              <div className="muted">{new Date(l.updatedAt).toLocaleString('de-DE')}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
